// src/config/documentSecurity.js

/**
 * Document Security Level Configuration
 *
 * Rules are fetched from the backend API (managed by superadmin).
 * Hardcoded defaults are used until the first successful API fetch.
 */

import apiService from '../services/api'

// Hardcoded defaults (used before API fetch completes)
const DEFAULT_RULES = {
  red: ['owner', 'admin'],
  yellow: ['owner', 'admin', 'diamond', 'gold'],
  green: ['owner', 'admin', 'diamond', 'gold', 'silver']
}

// Mutable cached rules - updated by initializeSecurityRules()
let _cachedRules = { ...DEFAULT_RULES }
let _initialized = false

// Role hierarchy (highest to lowest access)
export const ROLE_HIERARCHY = ['owner', 'admin', 'diamond', 'gold', 'silver']

// Security level hierarchy (highest to lowest security)
export const SECURITY_LEVEL_HIERARCHY = ['red', 'yellow', 'green']

// Security level display information
export const SECURITY_LEVEL_INFO = {
  red: {
    title: 'Confidential',
    description: 'Highly sensitive documents',
    color: 'red',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: '🔴'
  },
  yellow: {
    title: 'Restricted',
    description: 'Limited access documents',
    color: 'yellow',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    icon: '🟡'
  },
  green: {
    title: 'General',
    description: 'Standard access documents',
    color: 'green',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: '🟢'
  }
}

/**
 * Fetch security rules from the backend API and cache them.
 * Called once at app startup. Falls back to defaults on error.
 */
export async function initializeSecurityRules() {
  try {
    const response = await apiService.get('/security-rules/')
    if (response.data?.type === 'success' && response.data?.data?.rules) {
      _cachedRules = response.data.data.rules
      _initialized = true
    }
  } catch (e) {
    console.warn('Using default security rules:', e.message)
  }
}

/**
 * Force refresh security rules from API.
 * Useful after superadmin saves changes.
 */
export async function refreshSecurityRules() {
  return initializeSecurityRules()
}

/**
 * Get the current active security rules (for read access).
 */
export const getSecurityRules = () => _cachedRules

/**
 * Check if security rules have been loaded from API.
 */
export const isSecurityRulesInitialized = () => _initialized

/**
 * Get the list of roles that can access documents with the given security level
 */
export const getAllowedRolesForSecurityLevel = (securityLevel) => {
  return _cachedRules[securityLevel?.toLowerCase()] || []
}

/**
 * Get the list of security levels that a user role can access
 */
export const getAllowedSecurityLevelsForRole = (userRole) => {
  const allowedLevels = []
  const userRoleLower = userRole?.toLowerCase()

  for (const [securityLevel, allowedRoles] of Object.entries(_cachedRules)) {
    if (allowedRoles.includes(userRoleLower)) {
      allowedLevels.push(securityLevel)
    }
  }

  return allowedLevels
}

/**
 * Check if a user role can access a specific security level
 */
export const canUserAccessSecurityLevel = (userRole, securityLevel) => {
  const allowedRoles = getAllowedRolesForSecurityLevel(securityLevel)
  return allowedRoles.includes(userRole?.toLowerCase())
}

/**
 * Get the highest security level a user role can access
 */
export const getHighestAllowedSecurityLevelForRole = (userRole) => {
  const allowedLevels = getAllowedSecurityLevelsForRole(userRole)

  for (const level of SECURITY_LEVEL_HIERARCHY) {
    if (allowedLevels.includes(level)) {
      return level
    }
  }

  return 'green'
}

/**
 * Get document security access permissions for a user role and document security level
 */
export const getDocumentSecurityAccess = (userRole, documentSecurityLevel, basePermissions = {}) => {
  const hasAccess = canUserAccessSecurityLevel(userRole, documentSecurityLevel)
  const role = userRole?.toLowerCase() || 'silver'

  return {
    canView: hasAccess && basePermissions.canView,
    canDownload: hasAccess && basePermissions.canView,
    canEdit: hasAccess && basePermissions.canEdit,
    canDelete: hasAccess && basePermissions.canDelete,
    canSee: hasAccess,
    restrictionMessage: hasAccess ? null : `Your ${role?.toUpperCase()} role cannot access ${documentSecurityLevel?.toUpperCase()} security documents`
  }
}

/**
 * Get security rules summary for debugging
 */
export const getSecurityRulesSummary = () => {
  return {
    rules: _cachedRules,
    roleHierarchy: ROLE_HIERARCHY,
    securityHierarchy: SECURITY_LEVEL_HIERARCHY,
    initialized: _initialized,
    summary: Object.entries(_cachedRules).reduce((acc, [level, roles]) => {
      acc[level] = {
        allowedRoles: roles,
        roleCount: roles.length,
        description: `${level.toUpperCase()} documents accessible by ${roles.length} role(s)`
      }
      return acc
    }, {})
  }
}
